import { Query } from 'apollo-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface DataMapperResult<Item> {
  items: Item[];
  totalCount: number | null
}

export type DataMapper<QueryData, Item> = (queryResult: QueryData) => DataMapperResult<Item>

export interface PaginatedConnectionParams<OrderKey, FilterData> {
  first?: number | null;
  last?: number | null;
  after?: string | null;
  before?: string | null;
  orderBy?: {
    key: OrderKey;
    asc?: boolean | null
  } | null
  count: boolean;
  filter?: FilterData
}

export class TableDataSource<QueryData, Item, OrderKey, FilterData> {
  private dataSubject = new BehaviorSubject<Item[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private totalCountSubject = new BehaviorSubject<number>(0);
  private query: Query<QueryData, PaginatedConnectionParams<OrderKey, FilterData>>;
  private dataMapper: DataMapper<QueryData, Item>;

  public loading$ = this.loadingSubject.asObservable();
  public totalCount$ = this.totalCountSubject.asObservable();
  public dataSubject$ = this.dataSubject.asObservable();

  constructor(
    query: Query<QueryData, PaginatedConnectionParams<OrderKey, FilterData>>,
    dataMapper: DataMapper<QueryData, Item>
  ) {
    this.query = query;
    this.dataMapper = dataMapper;
  }

  connect(): Observable<Item[]> {
    return this.dataSubject.asObservable();
  }

  disconnect(): void {
    this.dataSubject.complete();
    this.loadingSubject.complete();
  }

  loadData(pageIndex: number, pageSize: number, orderKey: OrderKey | null = null, orderAsc: boolean = true, count: boolean = false, filter: FilterData | null = null): void {
    this.loadingSubject.next(true);

    const queryParams: PaginatedConnectionParams<OrderKey, FilterData> = {
      first: pageIndex * pageSize,
      last: pageSize,
      count
    };

    if(orderKey) {
      queryParams.orderBy = {
        key: orderKey,
        asc: orderAsc
      }
    }

    if(filter) {
      queryParams.filter = filter;
    }

    this.query.fetch(queryParams)
      .pipe(map(queryResult => this.dataMapper(queryResult.data)))
      .subscribe(data => {
        if(data.totalCount) {
          this.totalCountSubject.next(data.totalCount);
        }
        this.dataSubject.next(data.items);
        this.loadingSubject.next(false);
      }
    );
  }
}
